import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError, from } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  ApplicationState, DnvMapInfo, LocationCoordinate,
  ProblemMenuConfig, SearchCoordinatesPayload, SearchItem,
  SearchLocation, SearchItemType, QuestionsPayload, ImageFile, QuestionTypes, ErrorExport,
  SnowLayer
} from '../store/application';

declare const GOOGLE_RECAPTCHA_SITE_KEY: string;
declare const grecaptcha: any;
declare const CORRELATION_TOKEN: string;

@Injectable({
  providedIn: 'root'
})
export class RappService {
  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Methods': '*'
    })
  };

  constructor(private httpClient: HttpClient) {}

  getProblemMenuConfig(): Observable<ProblemMenuConfig[]> {
    return this.httpClient.get<ProblemMenuConfig[]>(environment.problemsApiEndpointUrl + `/publicNewCaseMenuConfig`, this.httpOptions);
  }

  getProblemQuestions(problemMenuId: number): Observable<QuestionsPayload[]> {
    return this.httpClient.get<QuestionsPayload[]>(environment.problemsApiEndpointUrl + `/problemQuestions/${problemMenuId}`, this.httpOptions);
  }

  getLocationCoordinates(location: SearchCoordinatesPayload): Observable<any> {
    switch (location.type) {
      case SearchItemType.Address:
        return this.httpClient.get<any>(environment.gisUrl + 'CivicAddress/' + location.name, this.httpOptions);

      case SearchItemType.StreetName:
        return this.httpClient.get<LocationCoordinate>(
          `${environment.searchApiEndpointUrl}api/geoinfo/street/${location.name}`,
          this.httpOptions
        );

      case SearchItemType.Park:
        return this.httpClient.get<LocationCoordinate>(environment.gisUrl + 'Park/' + location.name, this.httpOptions);

      case SearchItemType.Intersection:
        return this.httpClient.get<LocationCoordinate>(environment.gisUrl + 'Intersections/' + location.name, this.httpOptions);
    }

    return throwError('Unexpected type ' + location.type);
  }

  getLocationFromCoordinates(latLng: DnvMapInfo): Observable<SearchLocation[]> {
    return this.httpClient.post<any>(environment.gisUrl + 'pubrap/', latLng, this.httpOptions);
  }

  doDnvGisAddressSearch(searchFieldText: string): Observable<SearchItem[]> {
    return this.httpClient.get<SearchItem[]>(environment.gisUrl + 'searchitems/' + searchFieldText);
  }

  doDnvAddressSearch(searchFieldText: string): Observable<SearchItem[]> {
    const url = `${environment.searchApiEndpointUrl}api/searchItems/${searchFieldText}`;
    return this.httpClient.get<SearchItem[]>(url);
  }

  postProblemStub(applicationState: ApplicationState, recaptchaToken: string, recaptchaAction: string): Observable<any> { // TODO type this
    const stub = {
      token: recaptchaToken,
      action: recaptchaAction,
      correlation: CORRELATION_TOKEN,
      selectedProblem: applicationState.selectedProblemMenuItem,
      selectedLatLng: applicationState.selectedLatLng,
      problemLocation: applicationState.problemLocation,
      problemLocationOrigin: applicationState.problemLocationOrigin
    };
    return this.httpClient.post<any>('api/problem_stub', stub, this.httpOptions);
  }

  generateNewProblemDesc(applicationState: ApplicationState) {
    let problemDesc = applicationState.problemDetails;
    let builtDescStr = "";

    applicationState.problemQuestions.forEach(question => {
      if (question.answer && question.questionType !== QuestionTypes.ConfirmationToggle) {
        builtDescStr += `${question.question}: ${question.answer}`;

        if (question.radioOtherOptionEntry) {
          builtDescStr += ` - ${question.radioOtherOptionEntry}`;
        }

        builtDescStr += '<br>';

      } else if (question.questionType === QuestionTypes.Checkbox && question.checkOptionAnswers?.some(optionAnswer => optionAnswer.answer)) {
        builtDescStr += `${question.question}: `
        let checkedItemsStr = '';

        if (question.questionType === QuestionTypes.Checkbox) {
          question.checkOptionAnswers.forEach(optionAnswer => {
            if (optionAnswer.answer) {
              checkedItemsStr += `${optionAnswer.option}, `;
            }
          });

          // remove trailing comma and whitespaces
          checkedItemsStr.replace(/,\s*$/, "");
        }

        builtDescStr += checkedItemsStr + '<br>';
      } else if (question.questionType === QuestionTypes.ConfirmationToggle) {
        builtDescStr += `${question.question}: ${question.confirmationStatus}`;
        builtDescStr += '<br>';
      }
    });

    if (builtDescStr.length > 0) {
      builtDescStr += '<br>';
    }
    const descTitle = problemDesc ? "Description:<br>" : "";
    builtDescStr += descTitle + problemDesc;
    return builtDescStr;
  }

  postSubmitProblem(applicationState: ApplicationState, recaptchaToken: string, recaptchaAction: string): Observable<any> { // TODO type this
    const stub = {
      token: recaptchaToken,
      action: recaptchaAction,
      correlation: CORRELATION_TOKEN,
      guid: applicationState.newProblemUniqueId,
      selectedProblem: applicationState.selectedProblemMenuItem,
      selectedLatLng: applicationState.selectedLatLng,
      problemLocation: applicationState.problemLocation,
      problemLocationOrigin: applicationState.problemLocationOrigin,
      contactInfo: {
        firstName: applicationState.contactInfo.firstName,
        lastName: applicationState.contactInfo.lastName,
        email: applicationState.contactInfo.email,
        street: applicationState.contactInfo.address,
        notificationsRequested: applicationState.contactInfo.notificationsRequested,
        phone: applicationState.contactInfo.phone.map(p => ({ Type_Id: p.phoneType, Number: p.phoneNumber }))
      },
      // contactInfo: Object.assign({}, applicationState.contactInfo, {
      //   phone: applicationState.contactInfo.phone.map(p => ({ Type_Id: p.phoneType, Number: p.phoneNumber }))
      // }),
      description: this.generateNewProblemDesc(applicationState)
    };
    return this.httpClient.post<any>('api/submit_problem', stub, this.httpOptions);
  }

  postAddPhoto(applicationState: ApplicationState, recaptchaToken: string, recaptchaAction: string, imageFile: ImageFile): Observable<any> {
    const stub = {
      token: recaptchaToken,
      action: recaptchaAction,
      correlation: CORRELATION_TOKEN,
      guid: applicationState.newProblemUniqueId,
      type: imageFile.file.type,
      data: imageFile.source,
      hash: imageFile.hash
    };
    const postAddPhotoOptions = {
      headers: this.httpOptions.headers,
      reportProgress: true,
      observe: 'events' as const
    };
    return this.httpClient.post<any>('api/add_photo', stub, postAddPhotoOptions);
  }

  postRemovePhoto(applicationState: ApplicationState, recaptchaToken: string, recaptchaAction: string, hash: string): Observable<any> {
    const stub = {
      token: recaptchaToken,
      action: recaptchaAction,
      correlation: CORRELATION_TOKEN,
      guid: applicationState.newProblemUniqueId,
      hash: hash
    };
    return this.httpClient.post<any>('api/remove_photo', stub, this.httpOptions);
  }

  getReCaptchaToken(action: string): Observable<any> {
    return from(grecaptcha.enterprise.execute(GOOGLE_RECAPTCHA_SITE_KEY, { action }));
  }

  postSaveSearchLog(recaptchaToken: string, recaptchaAction: string, searchText: string): Observable<any> {
    const stub = {
      token: recaptchaToken,
      action: recaptchaAction,
      correlation: CORRELATION_TOKEN,
      searchText
    };
    return this.httpClient.post<string>('api/save_search_log', stub, this.httpOptions);
  }

  getSnowLegend(): Observable<SnowLayer> {
    return this.httpClient.get<SnowLayer>('https://www.geoweb.dnv.org/arcgis/rest/services/Layers_Webtech_SnowRemovalRoute/MapServer/0?f=pjson');
  }
}
